// EmailOutreach.js
// Purpose: Email outreach dashboard showing campaign analytics and metrics
// Logic: Displays sidebar navigation with email outreach data cards showing leads, emails sent, replies, and positive replies

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ChevronDownIcon,
  X,
  Mail,
  Settings,
  Globe,
  Eye,
  EyeOff,
  Send,
  Inbox,
  HelpCircle,
  Server,
} from "lucide-react";
import { Modal } from "antd";
import { Tooltip } from "flowbite-react";
import { standardApiCall } from "../lib/apiClient";
// import { useLocation } from "wouter";

import { useLocation, useNavigate } from "react-router-dom";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import { clog, isUserOwnerOfWorkspace, getPlanDetails } from "../lib/utils";
import { Sliders } from "lucide-react";

// Function to generate analytics for connected providers
const generateEmailAnalytics = (providers) => {
  return providers.map((provider, index) => ({
    providerId: provider.id,
    emailId: provider.providerEmail,
    fromName: provider.fromName || "Unknown",
    providerType: provider.providerType,
    // totalLeads: Math.floor(Math.random() * 200) + 50, // Demo data - replace with real analytics
    // totalEmailSend: Math.floor(Math.random() * 500) + 100,
    // totalReplies: Math.floor(Math.random() * 100) + 20,
    // totalPositiveReplies: Math.floor(Math.random() * 50) + 10,
    totalLeads: provider.totalLeads,
    totalEmailSend: provider.totalEmailsSent,
    totalReplies: provider.totalReplies,
    totalPositiveReplies: provider.totalPositiveReplies,
    totalEmailSentToday: provider.total_email_sent_today || 0,
    emailSignature: provider.emailSignature || "",
    isActive: provider.isActive,
    // status: provider.status !== undefined ? provider.status : 1, // 1 = enabled, 0 = disabled
  }));
};

// Add Signature Modal Component
const AddSignatureModal = ({
  isOpen,
  onClose,
  onSave,
  loading,
  providerId,
  initialSignature = "",
}) => {
  const [signature, setSignature] = useState(initialSignature);

  useEffect(() => {
    setSignature(initialSignature);
  }, [initialSignature, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (signature.trim()) {
      onSave(providerId, signature.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[20px] border border-[#dbdbdb] p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-[600] text-[#2A2A2A] text-[18px]">
            {initialSignature ? "Edit" : "Add"} Email Signature
          </h3>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#2A2A2A] transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
              Signature *
            </label>
            <textarea
              className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px] min-h-[120px] resize-none"
              placeholder="Enter your email signature here..."
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 rounded-[15px] font-[500] text-[14px] text-[#6B7280] border border-[#dbdbdb] hover:bg-[#F9FAFB] transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`py-2 px-6 rounded-[15px] font-[600] text-[14px]  transition-colors ${
                loading || !signature.trim()
                  ? "text-white bg-gray-400 cursor-not-allowed"
                  : "text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
              }`}
              disabled={loading || !signature.trim()}
            >
              {loading ? "Saving..." : "Save Signature"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Inline Email Configuration Drawer Component (used only in this page)
const EmailConfigDrawer = ({
  isOpen,
  onClose,
  setSuccessFailureMessage,
  setOpenSuccessModal,
  setOpenFailureModal,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [showSMTPModal, setShowSMTPModal] = useState(false);

  const emailProviders = [
    {
      id: "gmail",
      name: "Gmail",
      icon: <Mail className="w-6 h-6" />,
      description: "Connect your Gmail account for email outreach",
      color: "text-[#DB4437]",
    },
    {
      id: "outlook",
      name: "Outlook",
      icon: <Mail className="w-6 h-6" />,
      description: "Connect your Outlook account for email outreach",
      color: "text-[#0078D4]",
    },
    {
      id: "smtp",
      name: "SMTP",
      icon: <Settings className="w-6 h-6" />,
      description: "Configure custom SMTP settings for your domain",
      color: "text-[#6377F3]",
    },
  ];

  // Fetch connected providers when drawer opens
  const fetchConnectedProviders = async () => {
    try {
      setLoadingProviders(true);

      // Get user_id from selected_workspace
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      const response = await standardApiCall(
        `email-providers/${selectedWorkspace?.user?.user_id}`,
        "GET",
        null,
        setLoadingProviders,
        setSuccessFailureMessage,
        () => {},
        setOpenFailureModal,
        navigate
      );
      if (!response.data.error) {
        setConnectedProviders(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch connected providers:", error);
    }
  };

  // Run on drawer open
  React.useEffect(() => {
    if (isOpen) {
      fetchConnectedProviders();
    }
  }, [isOpen]);

  const handleProviderConnect = async (providerId) => {
    if (providerId === "gmail") {
      try {
        const response = await standardApiCall(
          "auth/gmail/initiate",
          "POST",
          {
            userId: JSON.parse(localStorage.getItem("login_data"))?.resp_user
              ?.id,
          },
          setLoading,
          setSuccessFailureMessage,
          setOpenSuccessModal,
          setOpenFailureModal,
          navigate
        );

        if (!response.data.error) {
          // Redirect to Gmail OAuth
          window.location.href = response.data.authUrl;
        }
      } catch (error) {
        console.error("Failed to initiate Gmail auth:", error);
      }
    } else if (providerId === "outlook") {
      try {
        const response = await standardApiCall(
          "auth/outlook/initiate",
          "POST",
          {
            userId: JSON.parse(localStorage.getItem("login_data"))?.resp_user
              ?.id,
          },
          setLoading,
          setSuccessFailureMessage,
          setOpenSuccessModal,
          setOpenFailureModal,
          navigate
        );

        if (!response.data.error) {
          // Redirect to Outlook OAuth
          window.location.href = response.data.authUrl;
        }
      } catch (error) {
        console.error("Failed to initiate Outlook auth:", error);
      }
    } else if (providerId === "smtp") {
      setShowSMTPModal(true);
    }
  };

  const isProviderConnected = (providerType) => {
    return connectedProviders.some(
      (provider) => provider.providerType === providerType
    );
  };

  const handleSMTPSubmit = async (smtpData) => {
    try {
      const response = await standardApiCall(
        "auth/smtp/configure",
        "POST",
        {
          userId: JSON.parse(localStorage.getItem("login_data"))?.resp_user?.id, // Using userId 1 for demo
          ...smtpData,
        },
        setLoading,
        setSuccessFailureMessage,
        setOpenSuccessModal,
        setOpenFailureModal,
        navigate
      );

      if (!response.data.error) {
        clog("SMTP connected successfully");
        setShowSMTPModal(false);
        await fetchConnectedProviders();
        // Close the drawer after successful connection with a small delay to ensure state updates
        setTimeout(() => {
          onClose();
        }, 100);
      }
    } catch (error) {
      console.error("Failed to configure SMTP:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[#dbdbdb]">
          <h2 className="font-[700] text-[#2A2A2A] text-[24px]">
            Configure Email
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F0F6FF] rounded-[10px] transition-colors"
          >
            <X className="w-5 h-5 text-[#2A2A2A]" />
          </button>
        </div>

        {/* Content */}
        <div className="h-[calc(100%-80px)] overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="mb-6">
            <h3 className="font-[600] text-[#2A2A2A] text-[18px] mb-2">
              Choose Email Provider
            </h3>
            <p className="text-[#6B7280] text-[16px] font-[400]">
              Connect your preferred email provider to send emails from your
              custom domain
            </p>
          </div>

          {/* Available Provider Options */}
          <div className="space-y-4">
            <h3 className="font-[600] text-[#2A2A2A] text-[16px]">
              Available Providers
            </h3>
            {emailProviders.map((provider) => {
              const isConnected = isProviderConnected(provider.id);
              return (
                <div
                  key={provider.id}
                  className={`flex items-center border rounded-[20px] px-4 py-4 shadow-sm bg-white mb-4 transition-all ${
                    isConnected
                      ? "border-[#3BB82A] opacity-60"
                      : "border-[#dbdbdb] hover:border-[#5E78F4] hover:shadow-md cursor-pointer"
                  }`}
                >
                  <div
                    className={`bg-[#F0F6FF] w-12 h-12 flex items-center justify-center rounded-[10px] me-4 ${provider.color}`}
                  >
                    {provider.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-[600] text-[#2A2A2A] text-[18px]">
                      {provider.name}
                    </h4>
                    <p className="font-[400] text-[#6B7280] text-[14px]">
                      {isConnected ? "Already connected" : provider.description}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleProviderConnect(provider.id)}
                      disabled={isConnected || loading}
                      className={`py-2 px-4 rounded-[20px] font-[600] text-[16px] transition-all ${
                        isConnected
                          ? "bg-[#3BB82A] text-white cursor-not-allowed"
                          : loading
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
                      }`}
                    >
                      {isConnected
                        ? "Connected"
                        : loading
                        ? "Connecting..."
                        : "Connect"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Setup Guides Section */}
          <div className="mt-8 p-4 bg-[#FFF9E6] rounded-[20px] border border-[#FFE066]">
            <div className="flex items-start">
              <HelpCircle className="w-5 h-5 text-[#FF8C00] mt-1 mr-3" />
              <div className="flex-1">
                <h4 className="font-[600] text-[#2A2A2A] text-[16px] mb-2">
                  Need Help Setting Up?
                </h4>
                <p className="text-[#2A2A2A] text-[14px] font-[400] leading-relaxed mb-3">
                  Follow our step-by-step guides with screenshots to easily
                  connect your email accounts.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/guides/gmail-setup"
                    target="_blank"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E1E5E9] rounded-[10px] text-[#2A2A2A] text-[14px] font-[500] hover:bg-[#F8F9FA] transition-colors"
                  >
                    <Mail size={16} />
                    Gmail Setup Guide
                  </a>
                  <a
                    href="/guides/smtp-setup"
                    target="_blank"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E1E5E9] rounded-[10px] text-[#2A2A2A] text-[14px] font-[500] hover:bg-[#F8F9FA] transition-colors"
                  >
                    <Server size={16} />
                    SMTP Setup Guide
                  </a>
                  <a
                    href="/guides/outlook-setup"
                    target="_blank"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E1E5E9] rounded-[10px] text-[#2A2A2A] text-[14px] font-[500] hover:bg-[#F8F9FA] transition-colors"
                  >
                    <Mail size={16} />
                    Outlook Setup Guide
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-6 p-4 bg-[#F0F6FF] rounded-[20px] border border-[#CADFFF]">
            <div className="flex items-start">
              <Globe className="w-5 h-5 text-[#5E78F4] mt-1 mr-3" />
              <div>
                <h4 className="font-[600] text-[#2A2A2A] text-[16px] mb-2">
                  Custom Domain Email
                </h4>
                <p className="text-[#2A2A2A] text-[14px] font-[400] leading-relaxed">
                  After connecting your email provider, you'll be able to send
                  emails from your custom domain using our platform's email
                  logic and automation features.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SMTP Configuration Modal */}
      {showSMTPModal && (
        <SMTPModal
          onSubmit={handleSMTPSubmit}
          onClose={async () => {
            // await fetchConnectedProviders();
            setShowSMTPModal(false);
            onClose();
          }}
          loading={loading}
          setSuccessFailureMessage={setSuccessFailureMessage}
          setOpenSuccessModal={setOpenSuccessModal}
          setOpenFailureModal={setOpenFailureModal}
          navigate={navigate}
        />
      )}
    </>
  );
};

// Inline SMTP Configuration Modal Component (used only in this page)
const SMTPModal = ({
  onSubmit,
  onClose,
  loading,
  setSuccessFailureMessage,
  setOpenSuccessModal,
  setOpenFailureModal,
  navigate,
}) => {
  const [formData, setFormData] = useState({
    fromName: "",
    smtpEmail: "",
    emailUsername: "",
    emailPassword: "",
    smtpHost: "",
    smtpPort: "587",
    enableImap: false,
    imapHost: "",
    imapPort: "993",
  });
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Combined verify and configure handler
  const handleVerifyAndConnect = async (e) => {
    e.preventDefault();
    // basic validation
    if (
      !formData.fromName ||
      !formData.smtpEmail ||
      !formData.emailUsername ||
      !formData.emailPassword ||
      !formData.smtpHost
    ) {
      setSuccessFailureMessage("Please fill in all required fields");
      setOpenFailureModal("dismissible");
      return;
    }
    if (formData.enableImap && !formData.imapHost) {
      setSuccessFailureMessage("Please provide IMAP host when IMAP is enabled");
      setOpenFailureModal("dismissible");
      return;
    }
    // prepare data for both APIs
    const commonData = {
      ...formData,
      smtpUsername: formData.emailUsername,
      smtpPassword: formData.emailPassword,
      imapUsername: formData.emailUsername,
      imapPassword: formData.emailPassword,
      userId: JSON.parse(localStorage.getItem("login_data"))?.resp_user?.id, // Using userId 1 for demo
    };
    try {
      // 1. verify email
      const verifyPayload = {
        smtpEmail: formData.smtpEmail,
        smtpHost: formData.smtpHost,
        smtpPort: formData.smtpPort,
        smtpUsername: formData.emailUsername,
        smtpPassword: formData.emailPassword,
        enableImap: formData.enableImap,
        imapHost: formData.imapHost,
        imapPort: formData.imapPort,
        imapUsername: formData.emailUsername,
        imapPassword: formData.emailPassword,
      };
      const verifyResp = await standardApiCall(
        "auth/email/verify",
        "POST",
        verifyPayload,
        setVerifying,
        setSuccessFailureMessage,
        setOpenSuccessModal,
        setOpenFailureModal,
        navigate
      );
      if (verifyResp.data.error) {
        setSuccessFailureMessage(verifyResp.data.error);
        setOpenFailureModal("dismissible");
        return; // failure modal shown by API util
      }
      // 2. configure SMTP
      const configResp = await standardApiCall(
        "auth/smtp/configure",
        "POST",
        commonData,
        setVerifying,
        setSuccessFailureMessage,
        setOpenSuccessModal,
        setOpenFailureModal,
        navigate
      );
      if (configResp.data.error) {
        setSuccessFailureMessage(configResp.data.error);
        setOpenFailureModal("dismissible");
        return; // failure modal shown
      }
      // success
      setSuccessFailureMessage(
        "Email account verified and SMTP configured successfully!"
      );
      setOpenSuccessModal("dismissible");

      onClose();
    } catch (err) {
      clog(err);
      setSuccessFailureMessage(
        err?.response?.data?.message ||
          "An error occurred during verification or configuration."
      );
      setOpenFailureModal("dismissible");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[20px] border border-[#dbdbdb] p-6 max-w-md w-full mx-4  ">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-[600] text-[#2A2A2A] text-[18px]">
            Configure SMTP Settings
          </h3>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#2A2A2A] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleVerifyAndConnect}
          className="space-y-4 overflow-y-auto max-h-[70vh] p-1"
        >
          <div>
            <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
              From Name *
            </label>
            <input
              type="text"
              name="fromName"
              value={formData.fromName}
              onChange={handleChange}
              placeholder="Your Name or Company Name"
              className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
              required
            />
          </div>

          <div>
            <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="smtpEmail"
              value={formData.smtpEmail}
              onChange={handleChange}
              placeholder="your-email@domain.com"
              className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
              required
            />
          </div>

          <div>
            <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
              Username *
            </label>
            <input
              type="text"
              name="emailUsername"
              value={formData.emailUsername}
              onChange={handleChange}
              placeholder="username or email"
              className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
              required
            />
          </div>

          <div>
            <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="emailPassword"
                value={formData.emailPassword}
                onChange={handleChange}
                placeholder="password or app password"
                className="w-full px-4 py-3 pr-12 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-[#5E78F4] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                SMTP Host *
              </label>
              <input
                type="text"
                name="smtpHost"
                value={formData.smtpHost}
                onChange={handleChange}
                placeholder="smtp.gmail.com"
                className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                required
              />
            </div>
            <div>
              <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                SMTP Port *
              </label>
              <input
                type="number"
                name="smtpPort"
                value={formData.smtpPort}
                onChange={handleChange}
                placeholder="587"
                className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                required
              />
            </div>
          </div>

          {/* IMAP Configuration Toggle */}
          <div className="flex items-center space-x-3 pt-4 border-t border-[#dbdbdb]">
            <input
              type="checkbox"
              id="enableImap"
              name="enableImap"
              checked={formData.enableImap}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  enableImap: e.target.checked,
                }))
              }
              className="w-4 h-4 text-[#5E78F4] border-gray-300 rounded focus:ring-[#5E78F4]"
            />
            <label
              htmlFor="enableImap"
              className="text-[#2A2A2A] font-[500] text-[14px]"
            >
              Enable IMAP (for reading emails)
            </label>
          </div>

          {/* IMAP Configuration Fields */}
          {formData.enableImap && (
            <div className="space-y-4 pt-4 border-t border-[#dbdbdb]">
              <h4 className="font-[600] text-[#2A2A2A] text-[16px]">
                IMAP Settings
              </h4>
              <p className="text-[#6B7280] text-[12px] mb-3">
                IMAP will use the same username and password as SMTP
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                    IMAP Host *
                  </label>
                  <input
                    type="text"
                    name="imapHost"
                    value={formData.imapHost}
                    onChange={handleChange}
                    placeholder="imap.gmail.com"
                    className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                    required={formData.enableImap}
                  />
                </div>
                <div>
                  <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                    IMAP Port *
                  </label>
                  <input
                    type="number"
                    name="imapPort"
                    value={formData.imapPort}
                    onChange={handleChange}
                    placeholder="993"
                    className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                    required={formData.enableImap}
                  />
                </div>
              </div>
            </div>
          )}

          {/* <div className="bg-[#F0F6FF] rounded-[15px] p-4 border border-[#CADFFF]">
            <h4 className="font-[600] text-[#2A2A2A] text-[14px] mb-2">
              Common Email Settings:
            </h4>
            <div className="text-[#6B7280] text-[12px] space-y-1">
              <p>
                <strong>Gmail SMTP:</strong> smtp.gmail.com:587
              </p>
              <p>
                <strong>Gmail IMAP:</strong> imap.gmail.com:993
              </p>
              <p>
                <strong>Outlook SMTP:</strong> smtp-mail.outlook.com:587
              </p>
              <p>
                <strong>Outlook IMAP:</strong> outlook.office365.com:993
              </p>
              <p>
                <strong>Note:</strong> Use App Passwords for Gmail/Outlook
              </p>
            </div>
          </div> */}

          <div className="flex justify-end items-center pt-4 space-x-2">
            {/* verify button removed, combined into submit */}

            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 rounded-[15px] font-[500] text-[14px] text-[#6B7280] border border-[#dbdbdb] hover:bg-[#F9FAFB] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={verifying || loading}
              className={`py-2 px-6 rounded-[15px] font-[600] text-[14px]  ${
                verifying || loading
                  ? "bg-gray-400 cursor-not-allowed text-white transition-colors"
                  : "text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
              }`}
            >
              {verifying || loading
                ? "Verifying and Connecting..."
                : "Verify and Connect"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Inline Analytics Card Component (used only in this page)
const EmailAnalyticsCard = ({
  data,
  index,
  onDisconnect,
  setSuccessFailureMessage,
  setOpenSuccessModal,
  setOpenFailureModal,
  navigate,
  onAddSignatureClick,
  isOwner,
  onToggleStatus,
}) => {
  // State for toggle loading
  const [isToggling, setIsToggling] = React.useState(false);

  // Handle toggle status
  const handleToggleStatus = async (e) => {
    e.stopPropagation();
    if (!isOwner || isToggling) return;

    const newStatus = data.isActive === 1 ? 0 : 1;
    setIsToggling(true);

    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/email/provider_update`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          provider_id: data.providerId,
          workspace_id: selectedWorkspace?.workspace_id || "",
          status: newStatus.toString(),
        },
      });

      if (!response.data.error) {
        // Call parent callback to update the state
        if (onToggleStatus) {
          onToggleStatus(data.providerId, newStatus);
        }
        setSuccessFailureMessage(
          `Email provider ${
            newStatus === 1 ? "enabled" : "disabled"
          } successfully!`
        );
        setOpenSuccessModal("dismissible");
      } else {
        setSuccessFailureMessage(
          response.data.message || "Failed to update provider status."
        );
        setOpenFailureModal("dismissible");
      }
    } catch (error) {
      console.error("Error toggling provider status:", error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      } else {
        setSuccessFailureMessage(
          "Failed to update provider status. Please try again."
        );
        setOpenFailureModal("dismissible");
      }
    } finally {
      setIsToggling(false);
    }
  };
  const handleDisconnect = () => {
    if (!isOwner) {
      return; // Don't proceed if not owner
    }

    Modal.confirm({
      title: "Disconnect Email Provider",
      content: `Are you sure you want to disconnect ${data.emailId}? This action will remove access to send emails through this provider.`,
      okText: "Disconnect",
      cancelText: "Cancel",
      okButtonProps: {
        danger: true,
        style: {
          borderRadius: "10px",
          fontWeight: "600",
        },
      },
      cancelButtonProps: {
        style: {
          borderRadius: "10px",
          fontWeight: "500",
        },
      },
      onOk: async () => {
        try {
          const response = await standardApiCall(
            `email-providers/${data.providerId}`,
            "POST",
            null,
            () => {},
            setSuccessFailureMessage,
            setOpenSuccessModal,
            setOpenFailureModal,
            navigate
          );

          if (!response.data.error) {
            onDisconnect(data.providerId);
            setSuccessFailureMessage(
              "Email provider disconnected successfully!"
            );
            setOpenSuccessModal("dismissible");
          } else {
            setSuccessFailureMessage(
              "Failed to disconnect email provider. Please try again."
            );
            setOpenFailureModal("dismissible");
          }
        } catch (error) {
          console.error("Error disconnecting provider:", error);
          setSuccessFailureMessage(
            "Failed to disconnect email provider. Please try again."
          );
          setOpenFailureModal("dismissible");
        }
      },
    });
  };

  const handleCardClick = () => {
    // Navigate to email inbox page for this provider
    // window.location.href = `/email-campaigns/${data.providerId}`;
    // navigate(`/email-campaigns/${data.providerId}`);
    // encrypt the providerId before navigating
    let encrypt = btoa(data.providerId);
    navigate(`/email-campaigns/${encrypt}`);
    // let decrypt = atob(encrypt);
    // clog("encrypt", encrypt);
  };

  // State for 3-dots menu
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  // Close menu on outside click
  React.useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  return (
    <div
      onClick={handleCardClick}
      className="w-full bg-[#FAFBFC] rounded-[20px] border border-[#dbdbdb] p-4 mb-4 hover:bg-[#F8F9FB] hover:border-[#C7C7C7] hover:shadow-lg transition-all duration-300 cursor-pointer group relative"
    >
      {/* 3-dots menu trigger */}
      <div
        className="absolute top-4 right-4 z-20"
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="p-1 rounded-full hover:bg-gray-100 focus:outline-none"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Open menu"
        >
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="2" fill="#6B7280" />
            <circle cx="12" cy="12" r="2" fill="#6B7280" />
            <circle cx="12" cy="19" r="2" fill="#6B7280" />
          </svg>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white border border-[#dbdbdb] rounded-[10px] shadow-lg z-30 ">
            {isOwner ? (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  handleDisconnect();
                }}
                className="w-full py-2 px-3 rounded-[10px] font-[500] text-[14px] bg-[#FFF2F2] hover:bg-[#FEE2E2] text-[#EF4444] hover:text-[#DC2626] transition-all duration-200 border border-[#FEE2E2] hover:border-[#FECACA] flex items-center justify-center gap-2"
              >
                <X className="w-3.5 h-3.5" />
                Disconnect
              </button>
            ) : (
              <Tooltip
                content="This action can only be done by workspace owner"
                placement="left"
              >
                <button
                  className="w-full py-2 px-3 rounded-[10px] font-[500] text-[14px] bg-gray-200 text-gray-500 cursor-not-allowed flex items-center justify-center gap-2"
                  disabled
                >
                  <X className="w-3.5 h-3.5" />
                  Disconnect
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap ">
        {/* Email ID Card */}
        <div className=" bg-white rounded-[14px] p-4 shadow-sm group-hover:shadow-md transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center mb-4">
            <img
              className="w-[23px] h-[22px] mr-4"
              alt="Vector"
              src="/figmaAssets/vector-10.svg"
            />
            <span className="font-[500] text-[#2A2A2A] text-[16px]">
              Email ID
            </span>
          </div>
          <div className="font-[700] text-[#2A2A2A] py-2">{data.emailId}</div>
          <div className="font-[700] text-[#2A2A2A] py-2">
            {data.totalEmailSentToday}/25 emails sent today
          </div>
        </div>

        {/* Total Leads Card */}
        <div className=" bg-white rounded-[14px] p-4 shadow-sm group-hover:shadow-md transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center mb-4">
            <img
              className="w-[19px] h-[22px] mr-4"
              alt="Vector"
              src="/figmaAssets/vector-15.svg"
            />
            <span className="font-[500] text-[#2A2A2A] text-[16px]">
              Total Leads
            </span>
          </div>
          <div className="font-[700] text-[#5E78F4] text-[32px]">
            {data.totalLeads}
          </div>
        </div>

        {/* Total Emails Sent Card */}
        <div className=" bg-white rounded-[14px] p-4 shadow-sm group-hover:shadow-md transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center mb-4">
            <img
              className="w-[23px] h-[22px] mr-4"
              alt="Vector"
              src="/figmaAssets/vector-3.svg"
            />
            <span className="font-[500] text-[#2A2A2A] text-[16px]">
              Total email <br /> send
            </span>
          </div>
          <div className="font-[700] text-[#F14CE2] text-[32px]">
            {data.totalEmailSend}
          </div>
        </div>

        {/* Total Replies Card */}
        <div className=" bg-white rounded-[14px] p-4 shadow-sm group-hover:shadow-md transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center mb-4">
            <img
              className="w-[26px] h-[22px] mr-4"
              alt="Group"
              src={`/figmaAssets/group-${index + 1}.png`}
            />
            <span className="font-[500] text-[#2A2A2A] text-[16px]">
              Total replies
            </span>
          </div>
          <div className="font-[700] text-[#6377F3] text-[32px]">
            {data.totalReplies}
          </div>
        </div>

        {/* Total Positive Replies Card */}
        <div className=" bg-white rounded-[14px] p-4 shadow-sm group-hover:shadow-md transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center mb-4">
            <img
              className="w-6 h-6 mr-4"
              alt="Vector"
              src="/figmaAssets/vector.svg"
            />
            <span className="font-[500] text-[#2A2A2A] text-[16px]">
              Total positive <br /> replies
            </span>
          </div>
          <div className="font-[700] text-[#3BB82A] text-[32px] ">
            {data.totalPositiveReplies}
          </div>
        </div>

        <div className="flex items-center py-4 gap-4">
          {isOwner ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onAddSignatureClick) {
                  onAddSignatureClick(
                    data?.providerId,
                    data?.emailSignature || ""
                  );
                }
              }}
              className="py-2 px-4 h-fit rounded-[15px] font-[600] text-[14px] text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
            >
              {data?.emailSignature ? "Edit" : "Add"} Signature
            </button>
          ) : (
            <Tooltip
              content="This action can only be done by workspace owner"
              placement="top"
            >
              <button
                className="py-2 px-4 h-fit rounded-[15px] font-[600] text-[14px] bg-gray-200 text-gray-500 border border-gray-300 cursor-not-allowed"
                disabled
              >
                {data?.emailSignature ? "Edit" : "Add"} Signature
              </button>
            </Tooltip>
          )}

          {/* Toggle Enable/Disable Email Send */}
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-[#2A2A2A] text-[14px] font-[500]">
              {data.isActive === 1 ? "Enabled" : "Disabled"}
            </span>
            {isOwner ? (
              <button
                onClick={handleToggleStatus}
                disabled={isToggling}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#5E78F4] focus:ring-offset-2 ${
                  isToggling
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                } ${data.isActive === 1 ? "bg-[#3BB82A]" : "bg-gray-300"}`}
                aria-label={
                  data.isActive === 1
                    ? "Disable email sending"
                    : "Enable email sending"
                }
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    data.isActive === 1 ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            ) : (
              <Tooltip
                content="This action can only be done by workspace owner"
                placement="top"
              >
                <button
                  disabled
                  className={`relative inline-flex h-6 w-11 items-center rounded-full cursor-not-allowed opacity-50 ${
                    data.isActive === 1 ? "bg-[#3BB82A]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      data.isActive === 1 ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Test Email Modal Component
const TestEmailModal = ({
  isOpen,
  onClose,
  connectedProviders,
  setOpenFailureModal,
  setOpenSuccessModal,
  setSuccessFailureMessage,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [sending, setSending] = useState(false);
  const [emailThreads, setEmailThreads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Parse email sender format
  const parseEmailSender = (fromField) => {
    if (!fromField) return "Unknown Sender";

    // Extract name and email from "Name <email>" format
    const match = fromField.match(/^(.+?)\s*<(.+)>$/);
    if (match) {
      const name = match[1].trim();
      const email = match[2].trim();
      return `${name} (${email})`;
    }

    // Return as-is if it's just an email address
    return fromField;
  };

  useEffect(() => {
    if (isOpen && connectedProviders.length > 0) {
      setSelectedProvider(connectedProviders[0].id.toString());
    }
  }, [isOpen, connectedProviders]);

  useEffect(() => {
    if (selectedProvider) {
      fetchEmailThreads();
    }
  }, [selectedProvider]);

  const fetchEmailThreads = async () => {
    setLoading(true);
    try {
      // Fetch ALL test emails (both sent and received) from database
      // Note: Converting to standardApiCall would require additional state management
      // For now, keeping fetch for complex multi-step operations
      const testEmailsResponse = await fetch(
        `${process.env.REACT_APP_BASE_SIGNIN_URL}/email/api/test-emails`
      );
      const testEmailsData = await testEmailsResponse.json();
      const allTestEmails = testEmailsData.error
        ? []
        : testEmailsData.data || [];

      // Separate sent and received emails
      const sentEmails = allTestEmails.filter((email) => email.type === "sent");
      const receivedEmails = allTestEmails.filter(
        (email) => email.type === "received"
      );

      // Also fetch fresh emails from ALL providers to catch any new replies not yet in database
      let freshReceivedEmails = [];
      if (connectedProviders.length > 0 && sentEmails.length > 0) {
        try {
          // Check all connected providers for replies, not just the selected one
          const allProviderEmails = [];

          // Get workspace_id from localStorage
          const selectedWorkspace = JSON.parse(
            localStorage.getItem("selected_workspace") || "{}"
          );

          for (const provider of connectedProviders) {
            clog(
              `Fetching emails from provider ${provider.id} (${provider.providerType})`
            );
            try {
              const imapResponse = await fetch(
                `${process.env.REACT_APP_BASE_SIGNIN_URL}/email/api/emails/${
                  provider.id
                }?limit=50&workspace_id=${
                  selectedWorkspace?.workspace_id || ""
                }`
              );
              const imapData = await imapResponse.json();
              if (!imapData.error && imapData.data) {
                allProviderEmails.push(
                  ...imapData.data.map((email) => ({
                    ...email,
                    fetchedFromProvider: provider.id,
                    fetchedFromProviderType: provider.providerType,
                  }))
                );
              } else {
                clog(
                  `No emails found for provider ${provider.id}:`,
                  imapData.message
                );
              }
            } catch (providerError) {
              console.error(
                `Error fetching from provider ${provider.id}:`,
                providerError
              );
            }
          }

          if (allProviderEmails.length > 0) {
            // Filter received emails to only show replies to sent test emails
            const filteredReceivedEmails = allProviderEmails.filter((email) => {
              // Check if this received email is a reply to any sent email
              return sentEmails.some((sentEmail) => {
                // Clean email addresses by extracting just the email part
                const cleanEmail = (emailStr) => {
                  if (!emailStr) return "";
                  const match =
                    emailStr.match(/<([^>]+)>/) ||
                    emailStr.match(/([^\s<>]+@[^\s<>]+)/);
                  return match
                    ? match[1].toLowerCase()
                    : emailStr.toLowerCase();
                };

                // Use consistent field names (handle both from/to and fromEmail/toEmail)
                const sentFromEmail = cleanEmail(
                  sentEmail.fromEmail || sentEmail.from
                );
                const sentToEmail = cleanEmail(
                  sentEmail.toEmail || sentEmail.to
                );
                const receivedFromEmail = cleanEmail(email.from);
                const receivedToEmail = cleanEmail(email.to);

                // Match by subject (replies often have "Re:" prefix)
                const sentSubject = (sentEmail.subject || "")
                  .toLowerCase()
                  .replace(/^re:\s*/i, "")
                  .trim();
                const receivedSubject = (email.subject || "")
                  .toLowerCase()
                  .replace(/^re:\s*/i, "")
                  .trim();

                // Enhanced reply detection logic:
                // 1. The received email's FROM should match the sent email's TO
                // 2. The received email's TO should match or contain the sent email's FROM
                // 3. Subjects should be related (same base subject)
                const isReplyByEmails =
                  receivedFromEmail === sentToEmail &&
                  (receivedToEmail === sentFromEmail ||
                    receivedToEmail.includes(sentFromEmail.split("@")[0]));

                // More flexible subject matching for replies
                const isReplyBySubject =
                  sentSubject === receivedSubject ||
                  receivedSubject.includes(sentSubject) ||
                  (email.subject || "").toLowerCase().startsWith("re:") ||
                  (sentSubject.length > 0 &&
                    receivedSubject.length > 0 &&
                    (sentSubject.includes(
                      receivedSubject.split(" ").slice(0, 3).join(" ")
                    ) ||
                      receivedSubject.includes(
                        sentSubject.split(" ").slice(0, 3).join(" ")
                      )));

                // Also check for common test email patterns
                const isTestEmailReply =
                  (sentEmail.subject || "").toLowerCase().includes("test") &&
                  (email.subject || "").toLowerCase().includes("test");

                // Consider it a reply if email threading suggests it or it's clearly a test email exchange
                return (
                  isReplyByEmails &&
                  (isReplyBySubject || isTestEmailReply) &&
                  sentSubject.length > 0
                );
              });
            });

            // Convert filtered IMAP emails to consistent format
            freshReceivedEmails = filteredReceivedEmails.map((email, index) => {
              // Parse email content from MIME format
              const parseEmailContent = (rawContent) => {
                if (!rawContent) return "No content available";

                // Extract plain text content from MIME - get everything until "On [date]" quoted section
                const textMatch = rawContent.match(
                  /Content-Type: text\/plain.*?\r?\n\r?\n(.*?)(?=\r?\n\r?\nOn .+wrote:|\r?\nContent-Type:|$)/s
                );
                if (textMatch) {
                  let textContent = textMatch[1];

                  // Decode quoted-printable encoding
                  textContent = textContent
                    .replace(/=\r?\n/g, "") // Remove soft line breaks
                    .replace(/=([0-9A-F]{2})/g, (match, hex) =>
                      String.fromCharCode(parseInt(hex, 16))
                    ) // Decode hex
                    .replace(/=E2=80=AF/g, " ") // Decode common unicode spaces
                    .replace(/=3D/g, "=") // Decode equals signs
                    .trim();

                  // Clean up the content while preserving structure
                  const cleanReply = textContent
                    .replace(/\r?\n   -\r?\n\r?\n   /g, "\n• ") // Convert bullet point format
                    .replace(/\r?\n   -\r?\n/g, "") // Remove standalone bullet separators
                    .replace(/\r?\n\r?\n/g, "\n\n") // Normalize double line breaks
                    .replace(/\r?\n/g, "\n") // Normalize single line breaks
                    .replace(/\*([^*]+)\*/g, "$1") // Remove asterisk formatting
                    .replace(/\n{3,}/g, "\n\n") // Limit multiple line breaks
                    .trim();

                  // Return the cleaned reply if it's reasonable length
                  if (
                    cleanReply &&
                    cleanReply.length > 0 &&
                    cleanReply.length < 1000
                  ) {
                    return cleanReply;
                  }

                  // Fallback to original content with basic cleanup
                  return textContent.replace(/\r?\n/g, "\n").trim();
                }

                // Fallback to HTML content extraction
                const htmlMatch = rawContent.match(
                  /<div[^>]*dir="ltr"[^>]*>(.*?)<\/div>/
                );
                if (htmlMatch) {
                  const cleanText = htmlMatch[1]
                    .replace(/<br\s*\/?>/gi, "\n") // Convert breaks to newlines
                    .replace(/<[^>]+>/g, "") // Remove all HTML tags
                    .replace(/&gt;/g, ">")
                    .replace(/&lt;/g, "<")
                    .replace(/&amp;/g, "&")
                    .replace(/\n{3,}/g, "\n\n") // Limit multiple line breaks
                    .trim();

                  return cleanText.length > 800
                    ? cleanText.substring(0, 800) + "..."
                    : cleanText;
                }

                return "No content available";
              };

              return {
                id: `received-${selectedProvider}-${
                  email.messageId || email.id || email.uid || index
                }-${Date.now()}`,
                type: "received",
                from: email.from,
                to: email.to,
                subject: email.subject,
                content: parseEmailContent(
                  email.text || email.html || email.body
                ),
                timestamp: email.date || new Date().toISOString(),
                providerId: parseInt(selectedProvider),
              };
            });
          }
        } catch (imapError) {
          clog("IMAP not available for this provider:", imapError);
        }
      }

      // Combine all emails: database emails + fresh IMAP emails (avoiding duplicates)
      const allEmails = [...sentEmails, ...receivedEmails];

      // Add fresh IMAP emails if they're not already in database
      freshReceivedEmails.forEach((freshEmail) => {
        const exists = receivedEmails.some(
          (dbEmail) =>
            dbEmail.subject === freshEmail.subject &&
            dbEmail.from === freshEmail.from &&
            Math.abs(
              new Date(dbEmail.timestamp) - new Date(freshEmail.timestamp)
            ) < 60000 // Within 1 minute
        );
        if (!exists) {
          allEmails.push(freshEmail);
        }
      });

      // Filter emails to only show those related to the selected provider
      const selectedProviderId = parseInt(selectedProvider);
      const selectedProviderData = connectedProviders.find(
        (p) => p.id === selectedProviderId
      );

      let filteredEmails = [];
      if (selectedProviderData) {
        // Get the selected provider's email address for filtering
        const providerEmail = selectedProviderData.providerEmail;

        // Filter emails that are either:
        // 1. Sent FROM the selected provider, OR
        // 2. Received TO the selected provider (replies to emails sent from this provider)
        filteredEmails = allEmails.filter((email) => {
          const cleanEmail = (emailStr) => {
            if (!emailStr) return "";
            const match =
              emailStr.match(/<([^>]+)>/) ||
              emailStr.match(/([^\s<>]+@[^\s<>]+)/);
            return match ? match[1].toLowerCase() : emailStr.toLowerCase();
          };

          const emailFromAddress = cleanEmail(email.fromEmail || email.from);
          const emailToAddress = cleanEmail(email.toEmail || email.to);
          const providerEmailClean = providerEmail.toLowerCase();

          clog("🔍 Email filtering debug:", {
            emailId: email.id,
            emailType: email.type,
            emailFromAddress,
            emailToAddress,
            providerEmailClean,
            emailProviderId: email.providerId,
            selectedProviderId,
            subject: email.subject,
          });

          // Simplified filtering: just use the providerId match for now
          // This ensures emails stored with a specific provider ID are shown for that provider
          const isProviderMatch = email.providerId === selectedProviderId;

          // Additional email address matching for more robust filtering
          const isFromMatch = emailFromAddress === providerEmailClean;
          const isToMatch =
            emailToAddress === providerEmailClean ||
            emailToAddress.includes(providerEmailClean.split("@")[0]);

          let shouldShow = false;

          if (email.type === "sent") {
            shouldShow = isProviderMatch || isFromMatch;
          } else if (email.type === "received") {
            shouldShow = isProviderMatch || isToMatch;
          }

          clog("🔍 Filter result:", {
            shouldShow,
            isProviderMatch,
            isFromMatch,
            isToMatch,
          });

          return shouldShow;
        });
      }

      // Sort filtered emails by timestamp
      const sortedEmails = filteredEmails.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );

      setEmailThreads(sortedEmails);
    } catch (error) {
      console.error("Failed to fetch email threads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!selectedProvider || !toAddress || !emailContent) return;

    try {
      const response = await standardApiCall(
        "test-emails/send",
        "POST",
        {
          providerId: parseInt(selectedProvider),
          to: toAddress,
          subject: emailSubject || "Test Email",
          content: emailContent,
        },
        setSending,
        setSuccessFailureMessage,
        setOpenSuccessModal,
        setOpenFailureModal,
        navigate
      );

      if (!response.data.error) {
        // Clear form
        setToAddress("");
        setEmailSubject("");
        setEmailContent("");
        // Refresh threads
        fetchEmailThreads();
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
      <div className="bg-white rounded-[20px] w-[90vw] max-w-[1200px] h-[80vh] max-h-[800px] ">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
          <h2 className="font-[600] text-[#2A2A2A] text-[20px]">
            Test Email Send/Receive
          </h2>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#374151] transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(100%-80px)] ">
          {/* Left Panel - Send Email */}
          <div className="w-1/2 p-6 border-r border-[#E5E7EB] overflow-scroll">
            <h3 className="font-[600] text-[#2A2A2A] text-[18px] mb-4 flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Test Email
            </h3>

            <form onSubmit={handleSendEmail} className="space-y-4">
              {/* Email Provider Dropdown */}
              <div>
                <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  From Email Account *
                </label>
                <select
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                  required
                >
                  {connectedProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.providerEmail} (
                      {provider.providerType.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              {/* To Address */}
              <div>
                <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  To Address *
                </label>
                <input
                  type="email"
                  value={toAddress}
                  onChange={(e) => setToAddress(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                  required
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Test Email Subject"
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                />
              </div>

              {/* Email Content */}
              <div>
                <label className="block text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  Email Content *
                </label>
                <textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  placeholder="Write your test email content here..."
                  rows={8}
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px] resize-none"
                  required
                />
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={
                  sending || !selectedProvider || !toAddress || !emailContent
                }
                className={`w-full py-3 rounded-[15px] font-[600] text-[16px] transition-colors ${
                  sending || !selectedProvider || !toAddress || !emailContent
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
                }`}
              >
                {sending ? "Sending..." : "Send Test Email"}
              </button>
            </form>
          </div>

          {/* Right Panel - Email Threads */}
          <div className="w-1/2 p-6">
            <h3 className="font-[600] text-[#2A2A2A] text-[18px] mb-2 flex items-center gap-2">
              <Inbox className="w-5 h-5" />
              Email Thread
            </h3>
            <p className="text-[#6B7280] text-[13px] mb-2">
              Shows sent test emails and their replies only
            </p>

            <div className="h-[500px] overflow-y-auto space-y-3">
              {loading ? (
                <>
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="space-y-3">
                      {/* Sent Email Skeleton */}
                      <div className="p-4 rounded-[12px] border bg-[#F0F6FF] border-[#5E78F4] ml-8">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                            <div className="h-4 w-32 bg-gray-300 rounded skeleton-wave"></div>
                          </div>
                          <div className="h-4 w-24 bg-gray-300 rounded skeleton-wave"></div>
                        </div>
                        <div className="h-5 w-full bg-gray-300 rounded skeleton-wave mb-2"></div>
                        <div className="h-4 w-3/4 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      {/* Received Email Skeleton */}
                      <div className="p-4 rounded-[12px] border bg-[#F9FAFB] border-[#E5E7EB] mr-8">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                            <div className="h-4 w-32 bg-gray-300 rounded skeleton-wave"></div>
                          </div>
                          <div className="h-4 w-24 bg-gray-300 rounded skeleton-wave"></div>
                        </div>
                        <div className="h-5 w-full bg-gray-300 rounded skeleton-wave mb-2"></div>
                        <div className="h-4 w-3/4 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                    </div>
                  ))}
                </>
              ) : emailThreads.length > 0 ? (
                emailThreads.map((email) => (
                  <div
                    key={email.id}
                    className={`p-4 rounded-[12px] border ${
                      email.type === "sent"
                        ? "bg-[#F0F6FF] border-[#5E78F4] ml-8"
                        : "bg-[#F9FAFB] border-[#E5E7EB] mr-8"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {email.type === "sent" ? (
                          <Send className="w-4 h-4 text-[#5E78F4]" />
                        ) : (
                          <Mail className="w-4 h-4 text-[#6B7280]" />
                        )}
                        <span className="font-[500] text-[#2A2A2A] text-[14px]">
                          {email.type === "sent"
                            ? `To: ${email.toEmail || email.to || "Unknown"}`
                            : `From: ${parseEmailSender(
                                email.fromEmail || email.from
                              )}`}
                        </span>
                      </div>
                      <span className="text-[#6B7280] text-[12px]">
                        {new Date(email.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <h4 className="font-[500] text-[#374151] text-[14px] mb-2">
                      {email.subject}
                    </h4>
                    <div className="text-[#6B7280] text-[13px] leading-relaxed whitespace-pre-wrap">
                      {(() => {
                        // Try multiple possible field names for email content
                        const content =
                          email.content ||
                          email.body ||
                          email.text ||
                          email.textContent ||
                          email.plainText ||
                          email.html ||
                          email.htmlContent ||
                          "";

                        // Enhanced debug logging for content issues
                        clog("🔍 Email content debug:", {
                          emailId: email.id,
                          emailType: email.type,
                          emailSource: email.source,
                          hasContent: !!content,
                          contentLength: content ? content.length : 0,
                          fullContent: content, // Show full content for debugging
                          availableFields: Object.keys(email),
                          fromEmail: email.fromEmail || email.from,
                          subject: email.subject,
                          rawContentType: typeof content,
                          isProblematicOutlook:
                            email.subject === "Re: Outlook to M434" &&
                            content &&
                            content.includes("wrote:"),
                        });

                        // Clean and format content - extract only the actual reply for received emails
                        let cleanContent = content
                          ? content.toString().trim()
                          : "";

                        // Enhanced content cleaning for ALL emails (decode HTML entities)
                        if (cleanContent) {
                          // HTML entity decoding for all emails
                          cleanContent = cleanContent
                            .replace(/&nbsp;/g, " ")
                            .replace(/&amp;/g, "&")
                            .replace(/&lt;/g, "<")
                            .replace(/&gt;/g, ">")
                            .replace(/&quot;/g, '"')
                            .replace(/&#39;/g, "'")
                            .replace(/\r\n/g, "\n")
                            .replace(/\r/g, "\n");
                        }

                        // For received emails, extract only the actual reply (remove quoted content)
                        if (email.type === "received" && cleanContent) {
                          clog("🧹 Content before cleaning:", cleanContent);

                          const originalContent = cleanContent;

                          // Enhanced quoted content removal for better reply extraction

                          // Pattern 1: "On [date] ... wrote:" - most common email clients (but preserve short "On" words in content)
                          cleanContent = cleanContent.replace(
                            /On\s+\w+,\s+\d+\s+\w+,?\s+\d+.+?wrote:.*$/i,
                            ""
                          );

                          // Pattern 2: Gmail mobile pattern with "On [date], ... <email> wrote:" (with HTML entities)
                          cleanContent = cleanContent.replace(
                            /On .+?, .+? &lt;.+?&gt; wrote:.*$/i,
                            ""
                          );
                          cleanContent = cleanContent.replace(
                            /On .+?, .+? <.+?> wrote:.*$/i,
                            ""
                          );

                          // Pattern 3: Outlook patterns
                          cleanContent = cleanContent.replace(
                            /-----Original Message-----[\s\S]*$/i,
                            ""
                          );
                          cleanContent = cleanContent.replace(
                            /_+\s*From:[\s\S]*$/i,
                            ""
                          );

                          // Pattern 4: Different language patterns
                          cleanContent = cleanContent.replace(
                            /Am .+? schrieb[\s\S]*$/i,
                            ""
                          ); // German
                          cleanContent = cleanContent.replace(
                            /Le .+? a écrit[\s\S]*$/i,
                            ""
                          ); // French

                          // Pattern 5: Remove everything after "From:" header
                          cleanContent = cleanContent.replace(
                            /From:\s*.+[\s\S]*$/i,
                            ""
                          );

                          // Pattern 6: Remove quoted lines starting with >
                          cleanContent = cleanContent.replace(/\n>.*/g, "");

                          // Clean up multiple newlines and fix spacing issues
                          cleanContent = cleanContent
                            .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between lowercase and uppercase letters
                            .replace(/([a-zA-Z])([A-Z][a-z])/g, "$1 $2") // Handle cases like "ReplyText"
                            .replace(/\n{3,}/g, "\n\n")
                            .trim();

                          clog("🧹 Content after cleaning:", cleanContent);
                          clog(
                            "🧹 Content changed:",
                            originalContent !== cleanContent
                          );
                        }

                        // Additional cleaning for ALL emails that contain quoted content patterns
                        if (
                          cleanContent &&
                          (cleanContent.includes("wrote:") ||
                            cleanContent.includes("On "))
                        ) {
                          clog(
                            "🔧 Additional cleaning for quoted content in email:",
                            email.id
                          );
                          clog("🔧 Email subject:", email.subject);
                          clog("🔧 Before additional cleaning:", cleanContent);

                          const beforeAdditional = cleanContent;

                          // More aggressive patterns for inline quoted content (but more specific to avoid false matches)
                          cleanContent = cleanContent.replace(
                            /On\s+\w+,\s+\d+\s+\w+,?\s+\d+.+?wrote:.*$/i,
                            ""
                          );
                          cleanContent = cleanContent.replace(
                            /On\s+.+?,\s+.+?\s+<.+?>\s+wrote:.*$/i,
                            ""
                          );
                          cleanContent = cleanContent.replace(
                            /On\s+.+?,\s+.+?\s+&lt;.+?&gt;\s+wrote:.*$/i,
                            ""
                          );

                          cleanContent = cleanContent.trim();

                          clog("🔧 After additional cleaning:", cleanContent);
                          clog(
                            "🔧 Additional cleaning changed:",
                            beforeAdditional !== cleanContent
                          );
                        }

                        // Special handling for the specific problematic Outlook email
                        if (
                          email.subject === "Re: Outlook to M434" &&
                          cleanContent &&
                          cleanContent.length > 10
                        ) {
                          clog("🎯 SPECIAL OUTLOOK HANDLING:", {
                            subject: email.subject,
                            beforeSpecial: cleanContent,
                            containsWrote: cleanContent.includes("wrote:"),
                            containsOn: cleanContent.includes("On "),
                          });

                          // Force cleaning for this specific case with precise pattern matching
                          const specialBefore = cleanContent;
                          cleanContent = cleanContent.replace(
                            /On\s+Tue,\s+19\s+Aug,\s+2025,\s+4:00\s+pm.*$/i,
                            ""
                          );
                          cleanContent = cleanContent.replace(
                            /On\s+\w+,\s+\d+\s+\w+,?\s+\d+.+?wrote:.*$/i,
                            ""
                          );
                          cleanContent = cleanContent.trim();

                          clog("🎯 After special cleaning:", cleanContent);
                          clog(
                            "🎯 Special cleaning changed:",
                            specialBefore !== cleanContent
                          );
                        }

                        if (!cleanContent) {
                          return (
                            <div className="italic text-[#9CA3AF]">
                              {email.type === "received"
                                ? "[Email content could not be parsed]"
                                : "[No content]"}
                            </div>
                          );
                        }

                        // Final debug before display
                        if (email.subject === "Re: Outlook to M434") {
                          clog("🎯 FINAL DISPLAY CHECK:", {
                            subject: email.subject,
                            finalContent: cleanContent,
                            finalLength: cleanContent.length,
                            willDisplay: cleanContent.substring(0, 50) + "...",
                          });
                        }

                        return (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: cleanContent.replace(
                                /\*([^*]+)\*/g,
                                "<strong>$1</strong>"
                              ),
                            }}
                          />
                        );
                      })()}
                    </div>

                    {/* SMTP Response Data for sent emails */}
                    {email.type === "sent" &&
                      email.smtpResponse &&
                      (() => {
                        try {
                          const smtpResponse =
                            typeof email.smtpResponse === "string"
                              ? JSON.parse(email.smtpResponse)
                              : email.smtpResponse;

                          return (
                            <div className="mt-3 pt-3 border-t border-[#E0E9FF]">
                              <div className="mb-2">
                                <span className="font-[500] text-[#5E78F4] text-[12px]">
                                  SMTP Response:
                                </span>
                              </div>
                              <div className="bg-[#F8FAFF] rounded-[8px] p-3 space-y-1 text-[11px] font-mono">
                                {smtpResponse.messageId && (
                                  <div>
                                    <span className="text-[#6B7280]">
                                      Message ID:
                                    </span>{" "}
                                    <span className="text-[#2A2A2A]">
                                      {smtpResponse.messageId}
                                    </span>
                                  </div>
                                )}
                                {smtpResponse.response && (
                                  <div>
                                    <span className="text-[#6B7280]">
                                      Server Response:
                                    </span>{" "}
                                    <span className="text-[#2A2A2A]">
                                      {smtpResponse.response}
                                    </span>
                                  </div>
                                )}
                                {smtpResponse.accepted &&
                                  smtpResponse.accepted.length > 0 && (
                                    <div>
                                      <span className="text-[#6B7280]">
                                        Accepted:
                                      </span>{" "}
                                      <span className="text-green-600">
                                        {smtpResponse.accepted.join(", ")}
                                      </span>
                                    </div>
                                  )}
                                {smtpResponse.rejected &&
                                  smtpResponse.rejected.length > 0 && (
                                    <div>
                                      <span className="text-[#6B7280]">
                                        Rejected:
                                      </span>{" "}
                                      <span className="text-red-600">
                                        {smtpResponse.rejected.join(", ")}
                                      </span>
                                    </div>
                                  )}
                                {smtpResponse.envelope && (
                                  <div>
                                    <span className="text-[#6B7280]">
                                      Envelope:
                                    </span>{" "}
                                    <span className="text-[#2A2A2A]">
                                      From: {smtpResponse.envelope.from}, To:{" "}
                                      {smtpResponse.envelope.to?.join(", ")}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        } catch (error) {
                          console.error("Error parsing SMTP response:", error);
                          return null;
                        }
                      })()}
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-32">
                  <Mail className="w-12 h-12 text-[#6B7280] mb-2" />
                  <div className="text-[#6B7280] text-[14px] text-center">
                    No email threads yet.
                    <br />
                    Send a test email to see it here.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Handoff Emails Modal Component
const HandoffEmailsModal = ({
  isOpen,
  onClose,
  setSuccessFailureMessage,
  setOpenSuccessModal,
  setOpenFailureModal,
  navigate,
}) => {
  // Check if user is owner of workspace
  const isOwner = isUserOwnerOfWorkspace();

  const [emailInputs, setEmailInputs] = useState([
    { id: 1, value: "", isValid: true, errorMessage: "" },
  ]);
  const [handoffEmails, setHandoffEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    email: "",
  });
  const [isDeletingEmail, setIsDeletingEmail] = useState(false);

  // Fetch existing handoff emails
  const fetchHandoffEmails = async () => {
    try {
      setIsLoading(true);

      // Get workspace_id from localStorage
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      // Custom API call for reply-email-list without /email in route
      const config = {
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/reply-email-list`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      };

      const response = await axios(config);

      if (!response.data.error && response.data.data) {
        setHandoffEmails(response.data.data?.split(","));
        // setSuccessFailureMessage(response?.data?.message);
        // setOpenSuccessModal("dismissible");
      } else {
        // setSuccessFailureMessage(response?.data?.message);
        // setOpenFailureModal("dismissible");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch handoff emails:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setSuccessFailureMessage(
        error?.response?.data?.message || error?.message
      );
      setOpenFailureModal("dismissible");
      setIsLoading(false);
    }
  };

  // Fetch emails when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchHandoffEmails();
    }
  }, [isOpen]);

  const handleEmailInputChange = (id, value) => {
    setEmailInputs((prev) =>
      prev.map((input) =>
        input.id === id
          ? { ...input, value: value.trim(), isValid: true, errorMessage: "" }
          : input
      )
    );
  };

  const validateEmailInput = (id, value) => {
    if (!value.trim()) return;

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value.trim())) {
      setEmailInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? {
                ...input,
                isValid: false,
                errorMessage: "Please enter a valid email address",
              }
            : input
        )
      );
      return;
    }

    // Check for duplicate emails in inputs
    const duplicate = emailInputs.some(
      (input) => input.id !== id && input.value.trim() === value.trim()
    );
    if (duplicate) {
      setEmailInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? { ...input, isValid: false, errorMessage: "Email already added" }
            : input
        )
      );
      return;
    }

    // Check against already saved emails
    if (handoffEmails.includes(value.trim())) {
      setEmailInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? { ...input, isValid: false, errorMessage: "Email already saved" }
            : input
        )
      );
      return;
    }
  };

  const addMoreEmailInput = () => {
    const newId = Math.max(...emailInputs.map((input) => input.id)) + 1;
    setEmailInputs((prev) => [
      ...prev,
      { id: newId, value: "", isValid: true, errorMessage: "" },
    ]);
  };

  const removeEmailInput = (id) => {
    if (emailInputs.length > 1) {
      setEmailInputs((prev) => prev.filter((input) => input.id !== id));
    }
  };

  const isAllInputsValid = () => {
    return emailInputs.every(
      (input) => input.isValid && input.value.trim() !== ""
    );
  };

  const saveHandoffEmails = async () => {
    if (!isOwner) {
      setSuccessFailureMessage("Only workspace owner can add handoff emails");
      setOpenFailureModal("dismissible");
      return;
    }

    if (!isAllInputsValid()) return;

    setIsSaving(true);
    try {
      const validEmails = emailInputs
        .filter((input) => input.value.trim() !== "")
        .map((input) => input.value.trim());

      // Custom API call for add-reply-email without /email in route
      const config = {
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/add-reply-email`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          replyEmail: validEmails.join(","),
        },
      };

      const response = await axios(config);

      if (!response.data.error) {
        setEmailInputs([{ id: 1, value: "", isValid: true, errorMessage: "" }]);
        setSuccessFailureMessage(response?.data?.message);
        setOpenSuccessModal("dismissible");
        fetchHandoffEmails(); // Refresh the list
      } else {
        setSuccessFailureMessage(response?.data?.message);
        setOpenFailureModal("dismissible");
      }
      setIsSaving(false);
    } catch (error) {
      console.error("Error saving handoff emails", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setSuccessFailureMessage(
        error?.response?.data?.message || error?.message
      );
      setOpenFailureModal("dismissible");
      setIsSaving(false);
    }
  };

  const confirmDeleteEmail = async () => {
    if (!isOwner) {
      setDeleteConfirmModal({ open: false, email: "" });
      setSuccessFailureMessage(
        "Only workspace owner can delete handoff emails"
      );
      setOpenFailureModal("dismissible");
      return;
    }

    setIsDeletingEmail(true);
    try {
      // Custom API call for delete-reply-email without /email in route
      const config = {
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/delete-reply-email`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          deleteEmail: deleteConfirmModal.email,
        },
      };

      const response = await axios(config);

      if (!response.data.error) {
        setDeleteConfirmModal({ open: false, email: "" });
        setSuccessFailureMessage(response?.data?.message);
        setOpenSuccessModal("dismissible");
        fetchHandoffEmails(); // Refresh the list
      } else {
        setSuccessFailureMessage(response?.data?.message);
        setOpenFailureModal("dismissible");
      }
      setIsDeletingEmail(false);
    } catch (error) {
      console.error("Error deleting handoff email:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setSuccessFailureMessage(
        error?.response?.data?.message || error?.message
      );
      setOpenFailureModal("dismissible");
      setIsDeletingEmail(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[20px] w-[90vw]  h-[80vh] max-h-[700px] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
          <h2 className="font-[600] text-[#2A2A2A] text-[20px]">
            Handoff Emails
          </h2>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#374151] transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(100%-80px)]">
          {/* Left Panel - Add Emails */}
          <div className="w-1/2 p-6 border-r border-[#E5E7EB] overflow-y-auto">
            <div className="flex justify-between items-center ">
              <h3 className="font-[600] text-[#2A2A2A] text-[18px] ">
                Add Handoff Emails
              </h3>
              {isOwner ? (
                <button
                  onClick={addMoreEmailInput}
                  className="flex items-center gap-2 px-3 py-1 bg-[#20C520] text-white rounded-[10px] text-sm font-medium hover:bg-[#1ea01e] transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 1V15M1 8H15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add More
                </button>
              ) : (
                <Tooltip
                  content="This action can only be done by workspace owner"
                  placement="left"
                >
                  <button
                    disabled
                    className="flex items-center gap-2 px-3 py-1 bg-gray-300 text-gray-500 rounded-[10px] text-sm font-medium cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 1V15M1 8H15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Add More
                  </button>
                </Tooltip>
              )}
            </div>
            <p>
              Kwin will forward all positive replies to these email ids, so your
              Human Business Developer can further pursue these leads.
            </p>
            <div className="space-y-4 mt-6">
              {emailInputs.map((input, index) => (
                <div key={input.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex-1 pl-px pr-2 py-px bg-white rounded-[30px] shadow-[0px_3px_4px_0px_rgba(0,0,0,0.06)] outline outline-1 outline-offset-[-1px] flex justify-start items-center gap-5 ${
                        !input.isValid
                          ? "outline-red-500 shadow-red-200"
                          : "outline-neutral-200"
                      }`}
                    >
                      <div className="min-w-14 min-h-14 px-5 py-5 bg-blue-50 rounded-full outline outline-1 outline-offset-[-1px] outline-gray-50 inline-flex flex-col justify-center items-center">
                        <Mail className="w-5 h-5 text-zinc-800" />
                      </div>
                      <input
                        type="email"
                        value={input.value}
                        onChange={(e) =>
                          handleEmailInputChange(input.id, e.target.value)
                        }
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          handleEmailInputChange(input.id, val);
                          validateEmailInput(input.id, val);
                        }}
                        className="text-sky-700 text-lg font-medium w-full outline-none border-0 ring-0 focus:ring-0"
                        placeholder="Enter email address"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addMoreEmailInput();
                          }
                        }}
                        disabled={!isOwner}
                      />
                    </div>
                    {isOwner ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEmailInput(input.id);
                        }}
                        disabled={emailInputs.length === 1}
                        className={`${
                          emailInputs.length === 1
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <svg className="icon text-[22px] text-[#DF3939] ms-4">
                          <use href="#icon_script_delete"></use>
                        </svg>
                      </button>
                    ) : (
                      <Tooltip
                        content="This action can only be done by workspace owner"
                        placement="left"
                      >
                        <button disabled className="cursor-not-allowed">
                          <svg className="icon text-[22px] text-gray-400 ms-4">
                            <use href="#icon_script_delete"></use>
                          </svg>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  {!input.isValid && input.errorMessage && (
                    <p className="text-red-500 text-sm ml-20">
                      {input.errorMessage}
                    </p>
                  )}
                </div>
              ))}

              <div className="pt-4">
                {isOwner ? (
                  <button
                    type="button"
                    className={`w-full py-3 rounded-[20px] font-semibold text-[18px] transition-colors ${
                      !isAllInputsValid()
                        ? "opacity-50 cursor-not-allowed bg-gray-300 text-gray-500"
                        : "text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
                    }`}
                    onClick={saveHandoffEmails}
                    disabled={!isAllInputsValid() || isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Emails"}
                  </button>
                ) : (
                  <Tooltip
                    content="This action can only be done by workspace owner"
                    placement="top"
                  >
                    <button
                      type="button"
                      className="w-full py-3 rounded-[20px] font-semibold text-[18px] bg-gray-300 text-gray-500 cursor-not-allowed"
                      disabled
                    >
                      Save Emails
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Saved Emails */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <h3 className="font-[600] text-[#2A2A2A] text-[18px] mb-4">
              Saved Handoff Emails
            </h3>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="h-5 w-48 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-6 h-6 bg-gray-300 rounded skeleton-wave"></div>
                  </div>
                ))}
              </div>
            ) : handoffEmails.length > 0 ? (
              <div className="space-y-3">
                {handoffEmails.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-[#5E78F4]" />
                      <span className="text-[#2A2A2A] text-[14px] font-medium">
                        {email}
                      </span>
                    </div>
                    {isOwner ? (
                      <button
                        onClick={() =>
                          setDeleteConfirmModal({ open: true, email })
                        }
                        className="text-[#DF3939] hover:bg-red-50 p-1 rounded transition-colors"
                      >
                        <svg className="icon text-[18px]">
                          <use href="#icon_script_delete"></use>
                        </svg>
                      </button>
                    ) : (
                      <Tooltip
                        content="This action can only be done by workspace owner"
                        placement="left"
                      >
                        <button
                          disabled
                          className="text-gray-400 p-1 rounded cursor-not-allowed"
                        >
                          <svg className="icon text-[18px]">
                            <use href="#icon_script_delete"></use>
                          </svg>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32">
                <Mail className="w-12 h-12 text-[#6B7280] mb-2" />
                <div className="text-[#6B7280] text-[14px] text-center">
                  No handoff emails added yet.
                  <br />
                  Add emails to get started.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-md p-8 text-center max-w-sm mx-4">
              <div className="mb-4">
                <svg
                  className="w-6 h-6 text-red-500 mx-auto"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
              </div>
              <h3 className="text-[20px] font-semibold text-[#2A2A2A] mb-2">
                Are You Sure?
              </h3>
              <p className="text-[16px] text-[#2A2A2A] mb-6">
                Do you really want to delete this email address?
                <br />
                <span className="font-medium">{deleteConfirmModal.email}</span>
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() =>
                    setDeleteConfirmModal({ open: false, email: "" })
                  }
                  className="px-6 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteEmail}
                  disabled={isDeletingEmail}
                  className="px-6 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50 flex items-center justify-center min-w-[80px]"
                >
                  {isDeletingEmail ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ICP/PIS Threshold Modal Component (for paid users only)
const ICPPISThresholdModal = ({
  isOpen,
  onClose,
  setSuccessFailureMessage,
  setOpenSuccessModal,
  setOpenFailureModal,
  navigate,
  isOwner,
}) => {
  const [icpCutoff, setIcpCutoff] = useState("");
  const [pisCutoff, setPisCutoff] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [domain, setDomain] = useState("");

  // Icon component to use SVG symbols
  const Icon = ({ name, className }) => (
    <svg className={className}>
      <use href={`#icon_${name}`} />
    </svg>
  );

  // Fetch domain and current thresholds when modal opens
  const fetchDomainAndThresholds = async () => {
    setIsLoading(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      // Fetch domain info
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });

      if (!response.data.error && response.data.data) {
        const domainData = response.data.data;
        setDomain(domainData.domain || "");
        // Pre-fill existing thresholds if available
        if (domainData.icp_cutoff) {
          setIcpCutoff(domainData.icp_cutoff.toString());
        }
        if (domainData.pis_cutoff) {
          setPisCutoff(domainData.pis_cutoff.toString());
        }
      }
    } catch (error) {
      console.error("Failed to fetch domain:", error);
      setSuccessFailureMessage("Failed to fetch domain information.");
      setOpenFailureModal("dismissible");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      fetchDomainAndThresholds();
    }
  }, [isOpen]);

  const handleSaveThresholds = async (e) => {
    e.preventDefault();

    if (!domain) {
      setSuccessFailureMessage("No domain found. Please add a domain first.");
      setOpenFailureModal("dismissible");
      return;
    }

    if (!icpCutoff || !pisCutoff) {
      setSuccessFailureMessage(
        "Please enter both ICP and PIS threshold values."
      );
      setOpenFailureModal("dismissible");
      return;
    }

    // Validate threshold values (should be valid numbers)
    const icpValue = parseFloat(icpCutoff);
    const pisValue = parseFloat(pisCutoff);

    if (isNaN(icpValue) || isNaN(pisValue)) {
      setSuccessFailureMessage(
        "Please enter valid numeric values for thresholds."
      );
      setOpenFailureModal("dismissible");
      return;
    }

    if (icpValue < 0 || icpValue > 100 || pisValue < 0 || pisValue > 100) {
      setSuccessFailureMessage("Threshold values should be between 0 and 100.");
      setOpenFailureModal("dismissible");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/icp_pis_cutoff`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          domain: domain,
          icp_cutoff: icpCutoff,
          pis_cutoff: pisCutoff,
        },
      });

      if (!response.data.error) {
        setSuccessFailureMessage("ICP & PIS thresholds saved successfully!");
        setOpenSuccessModal("dismissible");
        onClose();
      } else {
        setSuccessFailureMessage(
          response.data.message || "Failed to save thresholds."
        );
        setOpenFailureModal("dismissible");
      }
    } catch (error) {
      console.error("Failed to save thresholds:", error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      } else {
        setSuccessFailureMessage(
          "Failed to save thresholds. Please try again."
        );
        setOpenFailureModal("dismissible");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[20px] border border-[#dbdbdb] p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-[600] text-[#2A2A2A] text-[18px]">
            Set Email Trigger Thresholds
          </h3>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#2A2A2A] transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="py-6 space-y-4">
            {/* Domain Info Skeleton */}
            <div className="space-y-2">
              <div className="h-4 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-10 w-full bg-gray-300 rounded skeleton-wave"></div>
            </div>
            {/* Threshold Inputs Skeleton */}
            {[1, 2].map((index) => (
              <div key={index} className="space-y-2">
                <div className="h-4 w-32 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-10 w-full bg-gray-300 rounded skeleton-wave"></div>
              </div>
            ))}
            {/* Buttons Skeleton */}
            <div className="flex justify-end gap-3 mt-6">
              <div className="h-10 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-10 w-24 bg-gray-300 rounded skeleton-wave"></div>
            </div>
          </div>
        ) : !domain ? (
          <div className="py-6">
            <div className="bg-[#FFF9E6] rounded-[15px] p-4 border border-[#FFE066]">
              <p className="text-[#2A2A2A] text-[14px] font-[400]">
                No domain found. Please add a domain in the Script/Domain
                settings first.
              </p>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={onClose}
                className="py-2 px-4 rounded-[15px] font-[500] text-[14px] text-[#6B7280] border border-[#dbdbdb] hover:bg-[#F9FAFB] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSaveThresholds}>
            <div className="mb-4">
              <div className="bg-[#F0F6FF] rounded-[15px] p-3 border border-[#CADFFF] mb-4">
                <p className="text-[#2A2A2A] text-[14px] font-[500]">
                  Domain: <span className="text-[#5E78F4]">{domain}</span>
                </p>
              </div>

              <p className="text-[#6B7280] text-[14px] font-[400] mb-4">
                Set the minimum ICP (Ideal Customer Profile) and PIS (Purchase
                Intent Score) thresholds to trigger automated emails.
              </p>

              <div className="mb-4">
                <label className="flex gap-1 text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  ICP Threshold *
                  <Tooltip
                    trigger="hover"
                    autoFocus={true}
                    style={"light"}
                    className="w-[20%]"
                    content={
                      "Ideal Customer Profile (ICP) score is on a scale of 0 to 100. The higher the better. Kwin compares the identified visitor with the ICP approved in the Knowledgebase section."
                    }
                  >
                    <Icon name="information" className="w-5 h-5" />
                  </Tooltip>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                  placeholder="e.g., 15.50"
                  value={icpCutoff}
                  onChange={(e) => setIcpCutoff(e.target.value)}
                  required
                  disabled={isSaving || !isOwner}
                />
                <p className="text-[#9CA3AF] text-[12px] mt-1">
                  Visitors with ICP score above this value will trigger emails.
                </p>
              </div>

              <div className="mb-4">
                <label className="flex gap-1 text-[#2A2A2A] font-[500] text-[14px] mb-2">
                  PIS Threshold *
                  <Tooltip
                    trigger="hover"
                    autoFocus={true}
                    style={"light"}
                    className="w-[20%]"
                    content={
                      "Purchase Intent Score (PIS) is on a scale of 0 to 100. The higher the better. Kwin scores this based on the identified visitor's browsing behavior on your website."
                    }
                  >
                    <Icon name="information" className="w-5 h-5" />
                  </Tooltip>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 rounded-[10px] border border-[#dbdbdb] focus:border-[#5E78F4] focus:outline-none text-[16px]"
                  placeholder="e.g., 10.50"
                  value={pisCutoff}
                  onChange={(e) => setPisCutoff(e.target.value)}
                  required
                  disabled={isSaving || !isOwner}
                />
                <p className="text-[#9CA3AF] text-[12px] mt-1">
                  Visitors with PIS score above this value will trigger emails.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="py-2 px-4 rounded-[15px] font-[500] text-[14px] text-[#6B7280] border border-[#dbdbdb] hover:bg-[#F9FAFB] transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              {/* show not allowed tooltip if not owner */}
              {isOwner ? (
                <button
                  type="submit"
                  className={`py-2 px-6 rounded-[15px] font-[600] text-[14px] disabled:brightness-75 disabled:cursor-not-allowed ${
                    isSaving
                      ? "text-white bg-gray-400 cursor-not-allowed"
                      : "text-transparent border border-[#F14CE2] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white transition-colors"
                  }`}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Thresholds"}
                </button>
              ) : (
                <Tooltip
                  content="This action can only be done by workspace owner"
                  placement="top"
                >
                  <button
                    type="submit"
                    className={`py-2 px-6 rounded-[15px] font-[600] text-[14px] disabled:brightness-75 disabled:cursor-not-allowed ${
                      isSaving
                        ? "text-white bg-gray-400 cursor-not-allowed"
                        : "text-transparent border border-[#F14CE2] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white transition-colors"
                    }`}
                    disabled={isSaving || !isOwner}
                  >
                    {isSaving ? "Saving..." : "Save Thresholds"}
                  </button>
                </Tooltip>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const EmailOutreach = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is owner of workspace
  const isOwner = isUserOwnerOfWorkspace();

  // Check if user is on a paid plan (plan_id !== 6 means paid)
  const isPaidUser = getPlanDetails(true) !== 6;

  const [loading, setLoading] = useState(false);
  const [isEmailConfigOpen, setIsEmailConfigOpen] = useState(false);
  const [isTestEmailOpen, setIsTestEmailOpen] = useState(false);
  const [isHandoffEmailsOpen, setIsHandoffEmailsOpen] = useState(false);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [emailAnalytics, setEmailAnalytics] = useState([]);

  // Add Signature Modal states
  const [isAddSignatureOpen, setIsAddSignatureOpen] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [currentSignature, setCurrentSignature] = useState("");

  // States for standardized API handling
  const [successFailureMessage, setSuccessFailureMessage] = useState("");
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openFailureModal, setOpenFailureModal] = useState(false);

  // ICP/PIS Threshold Modal state
  const [isICPPISThresholdOpen, setIsICPPISThresholdOpen] = useState(false);

  // Fetch connected providers on component mount
  const fetchConnectedProviders = async () => {
    try {
      setLoadingProviders(true);

      // Get user_id from selected_workspace
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      const response = await standardApiCall(
        `email-providers/${selectedWorkspace?.user?.user_id}`,
        "GET",
        null,
        setLoadingProviders,
        () => {},
        () => {},
        setOpenFailureModal,
        navigate
      );
      if (!response.data.error) {
        const providers = response.data.data || [];
        setConnectedProviders(providers);
        // Generate analytics for connected providers
        setEmailAnalytics(generateEmailAnalytics(providers));
      } else {
        setSuccessFailureMessage(response.data.message);
        setOpenFailureModal("dismissible");
      }
    } catch (error) {
      console.error("Failed to fetch connected providers:", error);
      setSuccessFailureMessage(
        "Failed to fetch connected providers. Please try again."
      );
      setOpenFailureModal("dismissible");
    }
  };

  // Check for OAuth callback success/error in URL
  const handleOAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const error = urlParams.get("error");
    const email = urlParams.get("email");

    if (success === "gmail_connected" && email) {
      // Show success message and refresh providers
      clog(`Gmail connected successfully: ${email}`);
      setSuccessFailureMessage(
        `Gmail account connected successfully: ${email}. You can now send and receive emails through your Gmail account.`
      );
      setOpenSuccessModal("dismissible");
      fetchConnectedProviders();
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (success === "outlook_connected" && email) {
      // Show success message and refresh providers
      clog(`Outlook connected successfully: ${email}`);
      setSuccessFailureMessage(
        `Outlook account connected successfully: ${email}. You can now send and receive emails through your Outlook account.`
      );
      setOpenSuccessModal("dismissible");
      fetchConnectedProviders();
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      console.error(`OAuth error: ${error}`);
      const details = urlParams.get("details");
      const message = urlParams.get("message");

      // Determine which provider based on error type or current context
      let providerName = "Email provider";
      if (error.includes("outlook") || error.includes("microsoft")) {
        providerName = "Outlook";
      } else if (error.includes("gmail") || error.includes("google")) {
        providerName = "Gmail";
      }

      let errorMessage = `${providerName} connection failed`;
      if (details) {
        console.error(`OAuth error details: ${details}`);
        errorMessage += `: ${details}`;
      }
      if (message) {
        console.error(`OAuth error message: ${message}`);
        errorMessage += ` (${message})`;
      }

      setSuccessFailureMessage(
        errorMessage +
          ". Please try again or contact support if the issue persists."
      );
      setOpenFailureModal("dismissible");
      // Clear error parameters after logging
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Run on component mount
  React.useEffect(() => {
    fetchConnectedProviders();
    handleOAuthCallback();
  }, []);

  const handleConfigureEmail = () => {
    setIsEmailConfigOpen(true);
  };

  const handleCloseEmailConfig = () => {
    setIsEmailConfigOpen(false);
    // Refresh providers when drawer closes
    fetchConnectedProviders();
  };

  const handleDisconnectProvider = (providerId) => {
    // Remove from connected providers
    setConnectedProviders((prev) => prev.filter((p) => p.id !== providerId));
    // Remove from analytics
    setEmailAnalytics((prev) =>
      prev.filter((p) => p.providerId !== providerId)
    );
  };

  // Add Signature Modal handlers
  const handleAddSignatureClick = (providerId, currentSig) => {
    setSelectedProviderId(providerId);
    setCurrentSignature(currentSig);
    setIsAddSignatureOpen(true);
  };

  const handleSaveSignature = async (providerId, signature) => {
    setSignatureLoading(true);
    try {
      const response = await standardApiCall(
        `email-providers/provider/${providerId}`,
        "POST",
        { emailSignature: signature },
        setSignatureLoading,
        setSuccessFailureMessage,
        setOpenSuccessModal,
        setOpenFailureModal,
        navigate
      );

      if (!response.data.error) {
        setIsAddSignatureOpen(false);
        setSelectedProviderId(null);
        setCurrentSignature("");
        // Refresh providers to get updated signature status
        await fetchConnectedProviders();
        setSuccessFailureMessage("Email signature saved successfully!");
        setOpenSuccessModal("dismissible");
      }
    } catch (error) {
      console.error("Failed to save signature:", error);
      setSuccessFailureMessage("Failed to save signature. Please try again.");
      setOpenFailureModal("dismissible");
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleCloseSignatureModal = () => {
    setIsAddSignatureOpen(false);
    setSelectedProviderId(null);
    setCurrentSignature("");
  };

  // Handle toggle provider status (enable/disable)
  const handleToggleProviderStatus = (providerId, newStatus) => {
    // Update the emailAnalytics state with the new status
    setEmailAnalytics((prev) =>
      prev.map((item) =>
        item.providerId === providerId ? { ...item, isActive: newStatus } : item
      )
    );
  };

  return (
    <div className="bg-white flex  min-h-screen">
      <main className="flex-1 ">
        <div className="flex  flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
          <h1 className="font-[700] text-[#2A2A2A] text-[24px]">
            Email Outreach Details
          </h1>

          <div className="flex gap-3">
            {/* <button
              className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-[#F0F6FF] text-[#5E78F4] border border-[#5E78F4] hover:bg-[#5E78F4] hover:text-white transition-colors"
              onClick={() => setIsTestEmailOpen(true)}
              disabled={connectedProviders.length === 0}
            >
              Test Email Send/Receive
            </button> */}
            {/* ICP/PIS Threshold Button - Only for paid users who are owners */}
            {isPaidUser && (
              <button
                className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-[#F0F6FF] text-[#5E78F4] border border-[#5E78F4] hover:bg-[#5E78F4] hover:text-white transition-colors flex items-center gap-2"
                onClick={() => setIsICPPISThresholdOpen(true)}
              >
                <Sliders className="w-5 h-5" />
                Set Thresholds
              </button>
            )}
            <button
              className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-[#F0F6FF] text-[#5E78F4] border border-[#5E78F4] hover:bg-[#5E78F4] hover:text-white transition-colors"
              onClick={() => setIsHandoffEmailsOpen(true)}
            >
              Handoff Emails
            </button>
            {isOwner ? (
              <button
                //className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white hover:transition hover:delay-75"
                className="py-2 px-6 rounded-[20px] font-[600] text-[18px]  text-transparent border border-[#F14CE2]  bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text hover:bg-clip-border hover:border-none hover:text-white  transition-colors"
                onClick={handleConfigureEmail}
              >
                Configure Email
              </button>
            ) : (
              <Tooltip
                content="This action can only be done by workspace owner"
                placement="top"
              >
                <button
                  className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-gray-200 text-gray-500 border border-gray-300 cursor-not-allowed"
                  disabled
                >
                  Configure Email
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* No Connected Providers Message */}
        {!loadingProviders && connectedProviders.length === 0 && (
          <div className="bg-[#FFF9E6] rounded-[20px] border border-[#FFE4A3] p-6 mb-8 ">
            <div className="flex items-start">
              <Settings className="w-6 h-6 text-[#F59E0B] mt-1 mr-4" />
              <div>
                <h3 className="font-[600] text-[#2A2A2A] text-[18px] mb-2">
                  No Email Providers Connected
                </h3>
                <p className="text-[#6B7280] text-[14px] font-[400] mb-3">
                  Connect your email provider (Gmail, Outlook, or SMTP) to start
                  sending emails from your custom domain.
                </p>
                <button
                  onClick={handleConfigureEmail}
                  className="py-2 px-4 rounded-[15px] font-[500] text-[14px] bg-[#5E78F4] text-white hover:bg-[#4A63D2] transition-colors"
                >
                  Connect Email Provider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Analytics Section */}
        <div className="space-y-5">
          {loadingProviders ? (
            <>
              {[1, 2].map((cardIndex) => (
                <div
                  key={cardIndex}
                  className="w-full bg-[#FAFBFC] rounded-[20px] border border-[#dbdbdb] p-4 mb-4 relative"
                >
                  {/* 3-dots menu skeleton */}
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-gray-300 rounded-full skeleton-wave"></div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {/* Email ID Card Skeleton */}
                    <div className="bg-white rounded-[14px] p-4 shadow-sm flex flex-col justify-between">
                      <div className="flex items-center mb-4">
                        <div className="w-[23px] h-[22px] bg-gray-300 rounded skeleton-wave mr-4"></div>
                        <div className="h-5 w-20 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave py-2"></div>
                      <div className="h-6 w-48 bg-gray-300 rounded skeleton-wave py-2"></div>
                    </div>

                    {/* Total Leads Card Skeleton */}
                    <div className="bg-white rounded-[14px] p-4 shadow-sm flex flex-col justify-between">
                      <div className="flex items-center mb-4">
                        <div className="w-[19px] h-[22px] bg-gray-300 rounded skeleton-wave mr-4"></div>
                        <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="h-10 w-20 bg-gray-300 rounded skeleton-wave"></div>
                    </div>

                    {/* Total Emails Sent Card Skeleton */}
                    <div className="bg-white rounded-[14px] p-4 shadow-sm flex flex-col justify-between">
                      <div className="flex items-center mb-4">
                        <div className="w-[23px] h-[22px] bg-gray-300 rounded skeleton-wave mr-4"></div>
                        <div className="h-5 w-28 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="h-10 w-20 bg-gray-300 rounded skeleton-wave"></div>
                    </div>

                    {/* Total Replies Card Skeleton */}
                    <div className="bg-white rounded-[14px] p-4 shadow-sm flex flex-col justify-between">
                      <div className="flex items-center mb-4">
                        <div className="w-[26px] h-[22px] bg-gray-300 rounded skeleton-wave mr-4"></div>
                        <div className="h-5 w-28 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="h-10 w-20 bg-gray-300 rounded skeleton-wave"></div>
                    </div>

                    {/* Total Positive Replies Card Skeleton */}
                    <div className="bg-white rounded-[14px] p-4 shadow-sm flex flex-col justify-between">
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 bg-gray-300 rounded skeleton-wave mr-4"></div>
                        <div className="h-5 w-32 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="h-10 w-20 bg-gray-300 rounded skeleton-wave"></div>
                    </div>

                    {/* Action Buttons Skeleton */}
                    <div className="flex items-center py-4 gap-4">
                      <div className="h-9 w-32 bg-gray-300 rounded-[15px] skeleton-wave"></div>
                      <div className="flex items-center gap-2">
                        <div className="h-5 w-16 bg-gray-300 rounded skeleton-wave"></div>
                        <div className="h-6 w-11 bg-gray-300 rounded-full skeleton-wave"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : emailAnalytics.length > 0 ? (
            emailAnalytics.map((data, index) => (
              <EmailAnalyticsCard
                key={data.providerId}
                data={data}
                index={index}
                onDisconnect={handleDisconnectProvider}
                setSuccessFailureMessage={setSuccessFailureMessage}
                setOpenSuccessModal={setOpenSuccessModal}
                setOpenFailureModal={setOpenFailureModal}
                navigate={navigate}
                onAddSignatureClick={handleAddSignatureClick}
                isOwner={isOwner}
                onToggleStatus={handleToggleProviderStatus}
              />
            ))
          ) : (
            <div className="w-full bg-[#FAFBFC] rounded-[20px] border border-[#dbdbdb] p-8 text-center">
              <div className="text-[#6B7280] text-[16px] font-[400] mb-4">
                No email analytics available
              </div>
              <div className="text-[#9CA3AF] text-[14px]">
                Connect an email provider to start tracking your outreach
                campaigns
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Test Email Modal */}
      {isTestEmailOpen && (
        <TestEmailModal
          isOpen={isTestEmailOpen}
          onClose={() => setIsTestEmailOpen(false)}
          connectedProviders={connectedProviders}
          setSuccessFailureMessage={setSuccessFailureMessage}
          setOpenSuccessModal={setOpenSuccessModal}
          setOpenFailureModal={setOpenFailureModal}
          navigate={navigate}
        />
      )}

      {/* Handoff Emails Modal */}
      {isHandoffEmailsOpen && (
        <HandoffEmailsModal
          isOpen={isHandoffEmailsOpen}
          onClose={() => setIsHandoffEmailsOpen(false)}
          setSuccessFailureMessage={setSuccessFailureMessage}
          setOpenSuccessModal={setOpenSuccessModal}
          setOpenFailureModal={setOpenFailureModal}
          navigate={navigate}
        />
      )}

      {/* ICP/PIS Threshold Modal - Only for paid users */}
      {isICPPISThresholdOpen && (
        <ICPPISThresholdModal
          isOpen={isICPPISThresholdOpen}
          onClose={() => setIsICPPISThresholdOpen(false)}
          setSuccessFailureMessage={setSuccessFailureMessage}
          setOpenSuccessModal={setOpenSuccessModal}
          setOpenFailureModal={setOpenFailureModal}
          navigate={navigate}
          isOwner={isOwner}
        />
      )}

      {/* Email Configuration Drawer */}
      <EmailConfigDrawer
        isOpen={isEmailConfigOpen}
        onClose={handleCloseEmailConfig}
        setOpenFailureModal={setOpenFailureModal}
        setOpenSuccessModal={setOpenSuccessModal}
        setSuccessFailureMessage={setSuccessFailureMessage}
      />

      {/* Add Signature Modal */}
      <AddSignatureModal
        isOpen={isAddSignatureOpen}
        onClose={handleCloseSignatureModal}
        onSave={handleSaveSignature}
        loading={signatureLoading}
        providerId={selectedProviderId}
        initialSignature={currentSignature}
      />

      {/* Success/Failure Modals */}
      <SuccessModal
        // isOpen={openSuccessModal}
        // message={successFailureMessage}
        // onClose={() => setOpenSuccessModal(false)}
        open={openSuccessModal}
        removeModal={() => setOpenSuccessModal(false)}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        // isOpen={openFailureModal}
        // message={successFailureMessage}
        // onClose={() => setOpenFailureModal(false)}
        open={openFailureModal}
        removeModal={() => setOpenFailureModal(false)}
        successFailureMessage={successFailureMessage}
      />
    </div>
  );
};
